// The pieces CyberWISE adds to the platform shell.
//
// The chrome, the rail and the mobile drawer are @wise/ui's (platform rule:
// every module portal uses the shared shell). CyberWISE was built on the
// FacilityWISE template, which hand-rolled its own; it moved onto @wise/ui on
// 2026-10-05. What the handoff designed that the platform shell does not have
// lives here: the rail icons, the connector-health pill in the chrome's right
// cluster, and the page header.

import type { ReactNode } from 'react';
import type { WiseNavIcon, WiseNavIconProps } from '@wise/ui';
import type { Connector } from '../lib/types';

function icon(paths: readonly string[]): WiseNavIcon {
  return function NavIcon({ size = 18, style }: WiseNavIconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={style}
        aria-hidden="true"
      >
        {paths.map((d) => (
          <path key={d} d={d} />
        ))}
      </svg>
    );
  };
}

// The handoff's own glyphs, unchanged.
export const NAV_ICONS = {
  posture: icon(['M3 13h4l3-8 4 16 3-8h4']),
  incidents: icon(['M12 3 2 20h20L12 3z', 'M12 9v5M12 17.5h.01']),
  controls: icon(['M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z', 'm9 12 2 2 4-4']),
  deadlines: icon(['M3 10h18M8 2v4M16 2v4', 'M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z']),
  requests: icon(['M9.5 9a2.6 2.6 0 0 1 5 1c0 1.7-2.5 2-2.5 3.5M12 17.5h.01', 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z']),
  vendors: icon(['M3 7h18v13H3z', 'M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2', 'M3 12h18']),
  awareness: icon(['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M4 21c0-4 3.6-6 8-6s8 2 8 6']),
  connectors: icon(['M9 2v6M15 2v6', 'M6 8h12v4a6 6 0 0 1-12 0V8z', 'M12 18v4']),
} as const;

export const BellIcon = icon(['M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0']);

/**
 * The connector-health pill. It states the real split rather than a cheerful
 * summary: a degraded connector must be visible on the landing screen without
 * a click, and so must an unconfigured one. Clicking it opens Admin →
 * Connectors for the people who can fix it.
 */
export function HealthPill({ connectors, onOpen }: { connectors: Connector[]; onOpen?: () => void }) {
  if (connectors.length === 0) return null;
  const healthy = connectors.filter((c) => c.state === 'healthy').length;
  const notConnected = connectors.filter((c) => c.state === 'not_connected').length;
  const degraded = connectors.filter((c) => c.state === 'degraded').length;
  const label = degraded
    ? `${degraded} connector${degraded === 1 ? '' : 's'} degraded`
    : notConnected
      ? `${healthy} of ${connectors.length} connectors reporting`
      : 'Connectors healthy';
  const dot = degraded ? '#e0700f' : notConnected ? '#b8860b' : '#1f8a5b';
  const title = connectors.map((c) => `${c.name}: ${c.state.replace('_', ' ')}`).join('\n');
  const body = (
    <>
      <span className="dot" style={{ background: dot }} />
      <span>{label}</span>
    </>
  );
  return onOpen ? (
    <button type="button" className="sync cw-sync-btn" title={title} onClick={onOpen}>
      {body}
    </button>
  ) : (
    <div className="sync" title={title}>
      {body}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="ph">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="sub">{subtitle}</div>}
      </div>
      {actions && (
        <div className="ph-r">
          <div className="ph-acts">{actions}</div>
        </div>
      )}
    </div>
  );
}
